import React, { useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import {
    TextField,
    Button,
    Grid,
    Card,
    CardContent,
    Typography,
    Snackbar,
    Alert,
    FormControl,
} from "@mui/material";

import { useContactUsSubmission } from "../../hooks/appHooks";

const ContactFormSchema = Yup.object().shape({
    name: Yup.string()
        .required("Name is required")
        .min(2, "Name is too short")
        .max(50, "Name is too long"),
    email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
    subject: Yup.string()
        .required("Subject is required")
        .max(100, "Subject is too long"),
    message: Yup.string()
        .required("Message is required")
        .min(10, "Message is too short")
        .max(1000, "Message is too long"),
    phone_number: Yup.string()
        .matches(/^[0-9\-+()\s]*$/, "Phone number is not valid") // Optional phone number validation
        .max(15, "Phone number is too long"),
});


const ContactUsForm = () => {
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const onSuccess = () => {
        setSnackbarMessage("Your message has been sent successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
    };

    const onFailure = (error) => {
        setSnackbarMessage(error.message || "An error occurred. Please try again later.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
    };

    const { mutate: contactUsSubmission, isLoading: isSubmitting } = useContactUsSubmission({
        onSuccess,
        onFailure,
    });

    const handleFormSubmit = (values, { resetForm }) => {
        contactUsSubmission(values, {
            onSuccess: (resetForm())
        });
    };

    return (
        <Grid item xs={12} md={6}>
            <Card>
                <CardContent>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                        Send Us a Message
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        Have questions or specific requests? Send us a message below, and our team will respond within 24 hours.
                    </Typography>
                    <Formik
                        initialValues={{
                            name: "",
                            email: "",
                            subject: "",
                            message: "",
                            phone_number: "",
                        }}
                        validationSchema={ContactFormSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({ values, errors, touched, handleChange, handleBlur }) => (
                            <Form noValidate autoComplete="off">
                                <FormControl fullWidth margin="normal">
                                    <TextField
                                        id="name"
                                        name="name"
                                        label="Name"
                                        variant="outlined"
                                        value={values.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled={isSubmitting}
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name && errors.name}
                                    />
                                </FormControl>

                                <FormControl fullWidth margin="normal">
                                    <TextField
                                        id="contact-us-email"
                                        name="email"
                                        label="Email"
                                        variant="outlined"
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled={isSubmitting}
                                        error={touched.email && Boolean(errors.email)}
                                        helperText={touched.email && errors.email}
                                    />
                                </FormControl>

                                <FormControl fullWidth margin="normal">
                                    <TextField
                                        id="phone_number"
                                        name="phone_number"
                                        label="Phone Number (Optional)"
                                        variant="outlined"
                                        value={values.phone_number}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled={isSubmitting}
                                        error={touched.phone_number && Boolean(errors.phone_number)}
                                        helperText={touched.phone_number && errors.phone_number}
                                    />
                                </FormControl>

                                <FormControl fullWidth margin="normal">
                                    <TextField
                                        id="subject"
                                        name="subject"
                                        label="Subject"
                                        variant="outlined"
                                        value={values.subject}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled={isSubmitting}
                                        error={touched.subject && Boolean(errors.subject)}
                                        helperText={touched.subject && errors.subject}
                                    />
                                </FormControl>

                                <FormControl fullWidth margin="normal">
                                    <TextField
                                        id="message"
                                        name="message"
                                        label="Message"
                                        variant="outlined"
                                        multiline
                                        rows={4}
                                        value={values.message}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled={isSubmitting}
                                        error={touched.message && Boolean(errors.message)}
                                        helperText={touched.message && errors.message}
                                    />
                                </FormControl>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={isSubmitting}
                                    fullWidth
                                    sx={{ mt: 2, padding: "8px 0" }}
                                >
                                    Send Message
                                </Button>

                                <Snackbar
                                    open={snackbarOpen}
                                    autoHideDuration={3000}
                                    onClose={() => setSnackbarOpen(false)}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                >
                                    <Alert
                                        onClose={() => setSnackbarOpen(false)}
                                        severity={snackbarSeverity}
                                        sx={{ width: "100%" }}
                                    >
                                        {snackbarMessage}
                                    </Alert>
                                </Snackbar>
                            </Form>
                        )}
                    </Formik>
                </CardContent>
            </Card>
        </Grid>
    );
};

export default ContactUsForm;
